/**
 * Small illustrations for empty screens. They're drawn from theme colours
 * (accent, surfaces, muted text), so they follow the brand and light/dark mode.
 */

export type IllustrationName = 'friends' | 'conversations' | 'search' | 'pins' | 'voice' | 'channels';

const ACCENT = 'var(--clanfire-accent)';
const SOFT = 'var(--clanfire-accent-soft)';
const CARD = 'var(--clanfire-elevated)';
const SURFACE = 'var(--clanfire-hover)';
const LINE = 'var(--clanfire-border-strong)';
const MUTED = 'var(--clanfire-fg-muted)';

function Scene({ name }: { name: IllustrationName }) {
  switch (name) {
    case 'friends':
      return (
        <>
          <ellipse cx="80" cy="100" rx="58" ry="7" fill={SURFACE} />
          <circle cx="58" cy="44" r="16" fill={SOFT} stroke={ACCENT} strokeWidth="2.5" />
          <path d="M34 92c2-16 12-26 24-26s22 10 24 26" fill={SOFT} stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="104" cy="50" r="13" fill={CARD} stroke={LINE} strokeWidth="2.5" strokeDasharray="4 4" />
          <path d="M84 92c2-13 10-21 20-21s18 8 20 21" fill={CARD} stroke={LINE} strokeWidth="2.5" strokeDasharray="4 4" strokeLinecap="round" />
          <circle cx="126" cy="26" r="9" fill={ACCENT} />
          <path d="M126 21.5v9M121.5 26h9" stroke="var(--clanfire-accent-fg)" strokeWidth="2.2" strokeLinecap="round" />
        </>
      );
    case 'conversations':
      return (
        <>
          <ellipse cx="80" cy="100" rx="58" ry="7" fill={SURFACE} />
          <rect x="22" y="22" width="78" height="44" rx="14" fill={CARD} stroke={LINE} strokeWidth="2.5" />
          <path d="M40 66l-6 14 18-14" fill={CARD} stroke={LINE} strokeWidth="2.5" strokeLinejoin="round" />
          <path d="M38 38h46M38 50h30" stroke={MUTED} strokeWidth="3" strokeLinecap="round" opacity="0.5" />
          <rect x="70" y="50" width="68" height="36" rx="12" fill={SOFT} stroke={ACCENT} strokeWidth="2.5" />
          <path d="M122 86l6 11-16-11" fill={SOFT} stroke={ACCENT} strokeWidth="2.5" strokeLinejoin="round" />
          <circle cx="90" cy="68" r="3.5" fill={ACCENT} />
          <circle cx="104" cy="68" r="3.5" fill={ACCENT} opacity="0.7" />
          <circle cx="118" cy="68" r="3.5" fill={ACCENT} opacity="0.4" />
        </>
      );
    case 'search':
      return (
        <>
          <ellipse cx="80" cy="100" rx="58" ry="7" fill={SURFACE} />
          <rect x="28" y="18" width="70" height="74" rx="12" fill={CARD} stroke={LINE} strokeWidth="2.5" />
          <path d="M42 36h42M42 48h34M42 60h38M42 72h22" stroke={MUTED} strokeWidth="3" strokeLinecap="round" opacity="0.4" />
          <circle cx="102" cy="58" r="20" fill={SOFT} stroke={ACCENT} strokeWidth="3" />
          <path d="M116.5 72.5l14 14" stroke={ACCENT} strokeWidth="6" strokeLinecap="round" />
          <path d="M95 51l14 14M109 51l-14 14" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
        </>
      );
    case 'pins':
      return (
        <>
          <ellipse cx="80" cy="100" rx="58" ry="7" fill={SURFACE} />
          <rect x="30" y="30" width="100" height="60" rx="12" fill={CARD} stroke={LINE} strokeWidth="2.5" transform="rotate(-4 80 60)" />
          <path d="M48 52h56M48 64h40M48 76h48" stroke={MUTED} strokeWidth="3" strokeLinecap="round" opacity="0.4" transform="rotate(-4 80 60)" />
          <g transform="translate(112 26) rotate(30)">
            <path d="M-9 -14h18l-3 12 7 7h-26l7-7z" fill={ACCENT} />
            <path d="M0 5v16" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
          </g>
        </>
      );
    case 'voice':
      return (
        <>
          <ellipse cx="80" cy="100" rx="58" ry="7" fill={SURFACE} />
          <path d="M44 32a50 50 0 0 0 0 52M116 32a50 50 0 0 1 0 52" fill="none" stroke={LINE} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="3 6" />
          <path d="M56 40a34 34 0 0 0 0 36M104 40a34 34 0 0 1 0 36" fill="none" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" opacity="0.6" />
          <rect x="68" y="30" width="24" height="40" rx="12" fill={SOFT} stroke={ACCENT} strokeWidth="3" />
          <path d="M62 60a18 18 0 0 0 36 0M80 78v12M70 90h20" fill="none" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
        </>
      );
    case 'channels':
      return (
        <>
          <ellipse cx="80" cy="100" rx="58" ry="7" fill={SURFACE} />
          <rect x="30" y="20" width="100" height="22" rx="8" fill={SOFT} stroke={ACCENT} strokeWidth="2.5" />
          <path d="M42 26l-3 10M49 26l-3 10M37.5 29h13M36.5 33h13" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" />
          <path d="M60 31h52" stroke={ACCENT} strokeWidth="3" strokeLinecap="round" opacity="0.6" />
          <rect x="30" y="50" width="100" height="22" rx="8" fill={CARD} stroke={LINE} strokeWidth="2.5" strokeDasharray="5 5" />
          <rect x="30" y="80" width="100" height="14" rx="7" fill={CARD} stroke={LINE} strokeWidth="2.5" strokeDasharray="5 5" opacity="0.6" />
        </>
      );
  }
}

export function Illustration({ name, className }: { name: IllustrationName; className?: string }) {
  return (
    <svg viewBox="0 0 160 110" width="160" height="110" className={className} aria-hidden>
      <Scene name={name} />
    </svg>
  );
}
